import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { formatDateTime } from '../ui/format';
import { Badge, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  changePassword,
  listSessions,
  removeAvatar,
  resendVerification,
  revokeSession,
  saveAvatar,
  type SessionRow,
} from './api';
import { AvatarCropper, type CroppedImage } from './AvatarCropper';
import { Avatar } from '../ui/Avatar';

/**
 * The account, and the sessions that can reach it.
 *
 * The session list is the security feature on this screen: it is how somebody notices a login they
 * do not recognise, and how they end it. The current one cannot be revoked from here — that is what
 * signing out is for, and offering both invites the confusing outcome where the button that says
 * "revoke" logs you out.
 */
/**
 * The profile picture.
 *
 * Choosing a file opens the cropper; nothing is uploaded until the crop is confirmed, so a person
 * who picked the wrong photograph has not already put it on the server.
 */
function AvatarField(): React.JSX.Element {
  const { user, refreshUser } = useAuth();
  const input = useRef<HTMLInputElement>(null);

  const [chosen, setChosen] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (image: CroppedImage) => saveAvatar(image),
    onSuccess: async () => {
      setChosen(null);
      setError(null);
      await refreshUser();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: removeAvatar,
    onSuccess: async () => {
      setError(null);
      await refreshUser();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (chosen) {
    return (
      <div className="avatar-field">
        <AvatarCropper
          file={chosen}
          busy={save.isPending}
          onCancel={() => setChosen(null)}
          onDone={(image) => save.mutate(image)}
        />
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="avatar-field">
      <Avatar name={user?.name ?? ''} url={user?.avatar_url} />

      <div className="avatar-field__actions">
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => input.current?.click()}
        >
          {user?.avatar_url ? t('avatar.change') : t('avatar.choose')}
        </button>

        {user?.avatar_url && (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {t('avatar.remove')}
          </button>
        )}

        <p className="muted small">{t('avatar.hint')}</p>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) setChosen(file);

          // Cleared so choosing the same file twice still fires a change.
          event.target.value = '';
        }}
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function AccountPage(): React.JSX.Element {
  const { user } = useAuth();
  const client = useQueryClient();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);

  const sessions = useQuery({ queryKey: ['sessions'], queryFn: listSessions });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeSession(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['sessions'] }),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  /*
   * Another confirmation e-mail, from the one screen that says the address is unconfirmed.
   *
   * Nothing else on this page tells somebody what to do about that badge, and the alternative is
   * hunting for the original message — which, for an address confirmed months after sign-up, is
   * gone.
   */
  const resend = useMutation({
    mutationFn: resendVerification,
    onSuccess: () => {
      setError(null);
      setMessage(t('account.verificationSent'));
    },
    onError: (caught: unknown) => {
      setMessage(null);
      setError(messageFor(caught));
    },
  });

  const change = useMutation({
    mutationFn: () => changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: (result) => {
      setCurrent('');
      setNext('');
      setConfirmation('');
      setError(null);
      setReasons([]);
      setMessage(t('account.passwordChanged').replace('{n}', String(result.revoked_sessions)));
      void client.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (caught: unknown) => {
      setMessage(null);

      const payload = (caught as { payload?: { details?: { reasons?: string[] } } }).payload;

      setReasons(payload?.details?.reasons ?? []);
      setError(messageFor(caught));
    },
  });

  const columns: Column<SessionRow>[] = [
    {
      key: 'device',
      header: t('account.device'),
      render: (session) => (
        <span className="cell-stack">
          <span className="strong">{describe(session.user_agent)}</span>
          <span className="muted small">{session.ip ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'created',
      header: t('account.startedAt'),
      render: (session) => formatDateTime(session.created_at),
    },
    {
      key: 'used',
      header: t('account.lastUsed'),
      secondary: true,
      render: (session) => formatDateTime(session.last_used_at ?? session.created_at),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (session) =>
        session.current ? (
          <Badge tone="info">{t('account.thisDevice')}</Badge>
        ) : (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate(session.id)}
          >
            {t('account.revoke')}
          </button>
        ),
    },
  ];

  const mismatch = confirmation !== '' && next !== confirmation;

  return (
    <div className="view">
      <PageHeader title={t('nav.account')} description={user?.email ?? ''} />

      <div className="home-grid">
        <Panel title={t('account.profile')}>
          <AvatarField />

          <dl className="details">
            <dt>{t('field.name')}</dt>
            <dd>{user?.name}</dd>
            <dt>{t('field.email')}</dt>
            <dd>
              {user?.email}{' '}
              {user?.email_verified ? (
                <Badge tone="positive">{t('account.verified')}</Badge>
              ) : (
                <span className="cell-inline">
                  <Badge tone="warning">{t('account.unverified')}</Badge>

                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate()}
                  >
                    {resend.isPending ? t('common.saving') : t('account.resendVerification')}
                  </button>
                </span>
              )}
            </dd>
            <dt>{t('home.role')}</dt>
            <dd>{user?.role_key ?? '—'}</dd>
          </dl>
        </Panel>

        <Panel title={t('account.changePassword')}>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (!mismatch && current && next) change.mutate();
            }}
          >
            <label className="stack__field">
              <span>{t('account.currentPassword')}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            </label>

            <label className="stack__field">
              <span>{t('reset.newPassword')}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
            </label>

            <label className="stack__field">
              <span>{t('reset.confirmPassword')}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </label>

            {mismatch && (
              <p className="alert" role="alert">
                {t('reset.mismatch')}
              </p>
            )}

            {error && (
              <div className="alert" role="alert">
                <p>{error}</p>
                {reasons.length > 0 && (
                  <ul>
                    {reasons.map((reason) => (
                      <li key={reason}>{t(`password.reason.${reason}`)}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {message && (
              <p className="notice" role="status">
                {message}
              </p>
            )}

            <button
              type="submit"
              className="button button--primary button--compact"
              disabled={mismatch || !current || !next || change.isPending}
            >
              {change.isPending ? t('reset.submitting') : t('account.savePassword')}
            </button>
          </form>
        </Panel>
      </div>

      <Panel title={t('account.sessions')}>
        <DataTable
          tableKey="account"
          rows={sessions.data?.sessions}
          columns={columns}
          rowKey={(session) => session.id}
          isLoading={sessions.isLoading}
          error={sessions.error}
          onRetry={() => void sessions.refetch()}
          empty={{ title: t('account.noSessions') }}
        />
      </Panel>
    </div>
  );
}

/** A user agent string is unreadable; the browser and the platform are what anybody recognises. */
function describe(userAgent: string | null): string {
  if (!userAgent) return t('account.unknownDevice');

  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Chrome\//.test(userAgent)
      ? 'Chrome'
      : /Safari\//.test(userAgent)
        ? 'Safari'
        : /Firefox\//.test(userAgent)
          ? 'Firefox'
          : /curl/i.test(userAgent)
            ? 'curl'
            : t('account.unknownDevice');

  const platform = /Windows/.test(userAgent)
    ? 'Windows'
    : /Macintosh|Mac OS/.test(userAgent)
      ? 'macOS'
      : /Android/.test(userAgent)
        ? 'Android'
        : /iPhone|iPad/.test(userAgent)
          ? 'iOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : '';

  return platform ? `${browser} · ${platform}` : browser;
}
